import { useCallback, useEffect, useState } from "react";
import styles from "./ModelsUploadView.module.scss";
import { Button, Select, Textarea, View, ViewHeader } from "@/components";
import { UiFlash } from "../ui-flash";
import Link from '@/components/AppLink';
import { axiosInstance } from "@/helpers/axios";

type CatalogOption = { slug: string; displayName: string };

export const ModelsUploadView = () => {
  const [options, setOptions] = useState<CatalogOption[]>([]);
  const [targetSlug, setTargetSlug] = useState("");
  const [notes, setNotes] = useState("");
  const [flash, setFlash] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/v2/models/catalog");
      const items = data?.data?.items as CatalogOption[] | undefined;
      const list = Array.isArray(items) ? items : [];
      setOptions(list);
      setTargetSlug((prev) => prev || list[0]?.slug || "");
    } catch {
      setOptions([]);
      setFlash("Could not load catalog species.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFlash(
      "Upload flow is not wired on this page — use Add new model on the library or the create drawer."
    );
  };

  return (
    <View>
      <div className={styles["comp__content"]}>
        <ViewHeader
          heading="Upload 3D model"
          description="Choose the animal record, attach encrypted assets, and send the package to the review queue.<br />Prefer <strong>Add new model</strong> on the library for uploads wired to the pipeline."
        />

        <form className={styles["comp__form"]} onSubmit={handleSubmit}>
          <div className={styles["comp__select-block"]}>
            <span className={styles["comp__select-label"]} id="target-model-label">
              Target animal / model
            </span>
            <Select
              id="target-model"
              name="target-model"
              size="medium"
              variant="secondary"
              fullWidth
              value={targetSlug}
              onChange={setTargetSlug}
              disabled={loading || options.length === 0}
            >
              {options.map((m) => (
                <option key={m.slug} value={m.slug}>
                  {m.displayName} ({m.slug})
                </option>
              ))}
            </Select>
          </div>

          <div className={styles["comp__file-field"]}>
            <span className={styles["comp__file-label"]}>Model package</span>
            <input className={styles["comp__file-input"]} type="file" multiple />
          </div>

          <Textarea
            id="upload-notes"
            label="Notes for reviewers"
            value={notes}
            onChange={setNotes}
            rows={5}
            placeholder="What changed, known issues, QA checklist…"
          />

          <div className={styles["comp__actions"]}>
            <Button type="submit" size="medium" variant="primary">
              Send to review
            </Button>
            <Button href="/models" size="medium" variant="secondary">
              Back to library
            </Button>
          </div>
        </form>

        <p className={styles["comp__footer-note"]}>
          New versions go through chunking to R2 and review before going live.{" "}
          <Link href="/models/review">Open review queue</Link>
        </p>

        <UiFlash message={flash} onDismiss={() => setFlash(null)} />
      </div>
    </View>
  );
};
